import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Radio,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  Savings as SavingsIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeEditarProgramaContabilidad } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';

const PROGRAMA = 'avancessuplidor';
const PROGRAMA_MANTENIMIENTO = 'mantenimientoavancessuplidor';
/** Los avances a suplidor se registran siempre como pago al contado. */
const TIPO_PAGO_AVANCE = 'Contado';
const TIPO_DOCUMENTO_MOTOR_AVANCES = 'Avances a Suplidores';

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  try {
    const d = new Date(fecha);
    return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return fecha;
  }
};

const opcionLabel = (s) => {
  if (!s) return '';
  const id = s.id != null ? String(s.id) : '';
  const d = (s.descrip || '').trim();
  const r = (s.rnc || '').trim();
  return `${id}-${d}-${r}`;
};

const wanomesDesdeAnomesContabilidad = (anomes) => {
  const s = String(anomes ?? '').trim();
  const digits = s.startsWith('M') || s.startsWith('m') ? s.slice(1) : s;
  if (!/^\d{6}$/.test(digits)) return null;
  return digits;
};

const zanomesDesdeFechaStr = (fechaStr) => {
  const t = (fechaStr || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
  const [y, m] = t.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || m < 1 || m > 12) return null;
  return `${y}${String(m).padStart(2, '0')}`;
};

const validarMesContableAbierto = (fechaStr, wanomesAbierto) => {
  if (!wanomesAbierto) {
    return { ok: true };
  }
  const z = zanomesDesdeFechaStr(fechaStr);
  if (!z) {
    return { ok: false, message: 'Indique una fecha válida.' };
  }
  if (parseInt(z, 10) < parseInt(wanomesAbierto, 10)) {
    return {
      ok: false,
      message:
        'La fecha se encuentra en un mes cerrado (anterior al período contable abierto en varinicial: anoinicialcont / mesinicialcont).',
    };
  }
  return { ok: true };
};

const fechaLocalHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const escapeHtml = (s) => {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

/** HTML del comprobante para ventana de impresión (registro guardado). */
const buildComprobanteAvanceHtml = ({
  empresa,
  suplidor,
  fecha,
  numeroDocumento,
  valor,
  tipoPago,
  formaPago,
  referencia,
  ncf,
}) => {
  const v = parseFloat(valor);
  const valorFmt = fmtMoney(Number.isFinite(v) ? v : 0);
  const fechaTxt = formatearFecha(fecha);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Avance a suplidor</title>
<style>
  body { font-family: Arial, sans-serif; margin: 24px; color: #111; font-size: 13px; }
  h1 { font-size: 18px; margin-bottom: 8px; }
  .box { border: 1px solid #333; padding: 16px; margin-top: 16px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  td { padding: 6px 4px; vertical-align: top; }
  .label { width: 34%; font-weight: bold; }
  .line { border-top: 1px solid #000; flex: 1; text-align: center; padding-top: 8px; margin-top: 56px; }
</style></head><body>
  <h1>Comprobante de avance a suplidor</h1>
  <p style="margin:0;color:#444;">Documento para constancia y firma. Conserve un ejemplar.</p>
  <div class="box">
    <table>
      <tr><td class="label">Empresa</td><td>${escapeHtml(empresa)}</td></tr>
      <tr><td class="label">Suplidor</td><td>${escapeHtml(suplidor)}</td></tr>
      <tr><td class="label">Fecha</td><td>${escapeHtml(fechaTxt)}</td></tr>
      <tr><td class="label">Nº documento / recibo (id)</td><td>${escapeHtml(numeroDocumento)}</td></tr>
      <tr><td class="label">Valor del avance</td><td>RD$ ${escapeHtml(valorFmt)}</td></tr>
      <tr><td class="label">Tipo de pago</td><td>${escapeHtml(tipoPago)}</td></tr>
      <tr><td class="label">Forma de pago</td><td>${escapeHtml(formaPago)}</td></tr>
      <tr><td class="label">Referencia</td><td>${escapeHtml(referencia || '—')}</td></tr>
      <tr><td class="label">NCF / observación</td><td>${escapeHtml(ncf || '—')}</td></tr>
    </table>
  </div>
  <p style="margin-top:20px;line-height:1.5;">
    El suplidor declara haber recibido el monto indicado según la forma y tipo de pago señalados,
    en concepto de avance de fondos, sujeto a la conciliación con facturas y documentos de compra posteriores.
  </p>
  <div style="margin-top:48px;display:flex;justify-content:space-between;gap:24px;">
    <div class="line">Firma y sello — Empresa</div>
    <div class="line">Firma y sello — Suplidor</div>
  </div>
</body></html>`;
};

const AvancesSuplidor = ({ open = true, onClose, onGuardado } = {}) => {
  const { controller, selectedCompany, showSuccess, showError, showWarning } = useProductosMaterialContext();
  const {
    nombreNegocio,
    usuarioLog,
    claveLog,
    roleDetalles,
    modulos,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';
  const esDialogo = typeof onClose === 'function';

  const puedeLeer =
    tienePermisosModulo(roleDetalles, PROGRAMA) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_MANTENIMIENTO);
  const puedeModificar =
    puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA) ||
    puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_MANTENIMIENTO);
  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);

  const nombreEmpresa =
    selectedCompany?.name ||
    selectedCompany?.empresa ||
    (typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : '') ||
    'Empresa';

  const [suplidores, setSuplidores] = useState([]);
  const [suplidorSel, setSuplidorSel] = useState(null);
  const [cargandoSupl, setCargandoSupl] = useState(false);
  const [filas, setFilas] = useState([]);
  const [avanceListadoSel, setAvanceListadoSel] = useState(null);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [guardando, setGuardando] = useState(false);
  /** id del avance que se está eliminando, o null */
  const [eliminandoId, setEliminandoId] = useState(null);

  const [fecha, setFecha] = useState(() => fechaLocalHoy());
  const [valorStr, setValorStr] = useState('');
  const [formasPagoDb, setFormasPagoDb] = useState([]);
  const [cargandoFormas, setCargandoFormas] = useState(false);
  const [formaPago, setFormaPago] = useState('');
  const [referenciaPago, setReferenciaPago] = useState('');
  const [ncf, setNcf] = useState('');

  const [wanomesContableAbierto, setWanomesContableAbierto] = useState(null);

  const wanomesDesdeLogin = useMemo(
    () => wanomesDesdeAnomesContabilidad(selectedCompany?.anomescontabilidad),
    [selectedCompany?.anomescontabilidad]
  );

  const cargarVarinicialCont = useCallback(async () => {
    if (!puedeOperarDb) return;
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_obtenervarinicialcont`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.wanomes) {
        setWanomesContableAbierto(String(data.wanomes));
      } else {
        if (!wanomesDesdeLogin) {
          setWanomesContableAbierto(null);
        }
        if (data.Error) {
          showWarning(data.Error);
        }
      }
    } catch {
      if (!wanomesDesdeLogin) {
        setWanomesContableAbierto(null);
      }
    }
  }, [puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, wanomesDesdeLogin, showWarning]);

  useEffect(() => {
    if (wanomesDesdeLogin) {
      setWanomesContableAbierto(wanomesDesdeLogin);
      return;
    }
    setWanomesContableAbierto(null);
    if (puedeLeer && puedeOperarDb) {
      cargarVarinicialCont();
    }
  }, [wanomesDesdeLogin, selectedCompany?.db_empresa, puedeLeer, puedeOperarDb, cargarVarinicialCont]);

  const validacionFecha = useMemo(
    () => validarMesContableAbierto(fecha, wanomesContableAbierto),
    [fecha, wanomesContableAbierto]
  );

  const cargarSuplidores = useCallback(async () => {
    if (!puedeOperarDb) return;
    setCargandoSupl(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarsuplidores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo cargar suplidores');
        setSuplidores([]);
        return;
      }
      setSuplidores(data.suplidores || []);
    } catch {
      showError('Error de conexión al cargar suplidores');
      setSuplidores([]);
    } finally {
      setCargandoSupl(false);
    }
  }, [puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  const cargarFormasPago = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) {
      setFormasPagoDb([]);
      return;
    }
    setCargandoFormas(true);
    try {
      const response = await fetch(`${servidor}/configuracionbackend_listarformasdepago`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setFormasPagoDb(data.formas_pago || []);
      } else {
        setFormasPagoDb([]);
        if (data.Error) showError(data.Error);
      }
    } catch {
      setFormasPagoDb([]);
    } finally {
      setCargandoFormas(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  useEffect(() => {
    if (formasPagoDb.length === 0) {
      setFormaPago('');
      return;
    }
    setFormaPago((prev) => {
      if (prev && formasPagoDb.some((r) => (r.documento || '') === prev)) return prev;
      return formasPagoDb[0].documento || '';
    });
  }, [formasPagoDb]);

  const filaFormaPagoActual = useMemo(
    () => formasPagoDb.find((r) => (r.documento || '') === formaPago),
    [formasPagoDb, formaPago]
  );

  const requiereReferencia =
    Boolean(filaFormaPagoActual) &&
    String(filaFormaPagoActual.necesita_referencia || 'N').toUpperCase() === 'S';

  useEffect(() => {
    if (!requiereReferencia) {
      setReferenciaPago('');
    }
  }, [requiereReferencia]);

  const cargarAvances = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) return;
    setCargandoLista(true);
    try {
      const body = {
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        limite: 150,
      };
      if (suplidorSel?.id != null) {
        body.suplidor_id = String(suplidorSel.id);
      }
      const response = await fetch(`${servidor}/contabilidadbackend_listaravancesuplidor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setFilas(data.avances || []);
      } else {
        setFilas([]);
        showError(data.Error || 'No se pudo cargar el listado de avances');
      }
    } catch {
      setFilas([]);
      showError('Error de conexión al cargar avances');
    } finally {
      setCargandoLista(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, suplidorSel, showError]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      cargarSuplidores();
      cargarFormasPago();
    }
  }, [
    puedeLeer,
    puedeOperarDb,
    selectedCompany?.db_empresa,
    cargarSuplidores,
    cargarFormasPago,
  ]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      cargarAvances();
    }
  }, [puedeLeer, puedeOperarDb, suplidorSel, cargarAvances]);

  useEffect(() => {
    if (avanceListadoSel?.id == null) return;
    if (!filas.some((r) => r.id === avanceListadoSel.id)) {
      setAvanceListadoSel(null);
    }
  }, [filas, avanceListadoSel]);

  const abrirVentanaImpresion = useCallback((html) => {
    const w = window.open('', '_blank', 'width=820,height=900');
    if (!w) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
      w.print();
    }, 250);
  }, [showError]);

  const imprimirAvanceGuardado = useCallback(() => {
    const row = avanceListadoSel;
    if (!row) {
      showError('Seleccione un documento del listado (clic en la fila o en el selector).');
      return;
    }
    const html = buildComprobanteAvanceHtml({
      empresa: nombreEmpresa,
      suplidor: (row.nombre_suplidor || '').trim() || String(row.suplidor || ''),
      fecha: row.fecha,
      numeroDocumento: String(row.id ?? ''),
      valor: row.valor,
      tipoPago: (row.tipodepago || '').trim() || TIPO_PAGO_AVANCE,
      formaPago: (row.banco || '').trim() || '—',
      referencia: (row.referencia || '').trim() || '—',
      ncf: (row.ncf || '').trim() || '—',
    });
    abrirVentanaImpresion(html);
  }, [avanceListadoSel, nombreEmpresa, showError, abrirVentanaImpresion]);

  const handleEliminarAvance = useCallback(
    async (row, ev) => {
      if (ev) {
        ev.stopPropagation();
        ev.preventDefault();
      }
      if (!puedeModificar) {
        showError('No tiene permiso para modificar');
        return;
      }
      if (!row?.id) {
        return;
      }
      const idStr = String(row.id);
      const msg =
        `¿Eliminar el avance Nº doc. (id) ${idStr} del suplidor "${(row.nombre_suplidor || row.suplidor || '').trim() || '—'}"?\n\n` +
        'No podrá eliminarlo si el avance está aplicado a una factura CxP que ya tiene pagos registrados (vplicado).';
      if (!window.confirm(msg)) return;

      if (!selectedCompany?.db_empresa) {
        showError('Servidor o empresa no disponible');
        return;
      }
      setEliminandoId(row.id);
      try {
        const response = await fetch(`${servidor}/contabilidadbackend_eliminaravancesuplidor`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            avance_id: row.id,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          showError(data.Error || 'No se pudo eliminar el avance');
          return;
        }
        showSuccess(data.mensaje || 'Avance eliminado');
        setAvanceListadoSel((prev) => (prev?.id === row.id ? null : prev));
        await cargarAvances();
      } catch {
        showError('Error de conexión al eliminar');
      } finally {
        setEliminandoId(null);
      }
    },
    [puedeModificar, servidor, selectedCompany, usuarioLog, claveLog, showError, showSuccess, cargarAvances]
  );

  const handleGuardar = useCallback(async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar');
      return;
    }
    if (!selectedCompany?.db_empresa) {
      showError('Servidor o empresa no disponible');
      return;
    }
    const vFecha = validarMesContableAbierto(fecha, wanomesContableAbierto);
    if (!vFecha.ok) {
      showError(vFecha.message);
      return;
    }
    if (!suplidorSel?.id) {
      showError('Seleccione un suplidor');
      return;
    }
    const rawValor = (valorStr || '').trim().replace(',', '.');
    const valorNum = parseFloat(rawValor);
    if (!Number.isFinite(valorNum) || valorNum <= 0) {
      showError('Indique un valor válido mayor que cero');
      return;
    }
    if (!formaPago || formasPagoDb.length === 0) {
      showError('Configure y seleccione una forma de pago');
      return;
    }
    if (requiereReferencia && !(referenciaPago || '').trim()) {
      showError('La forma de pago exige referencia');
      return;
    }

    setGuardando(true);
    try {
      const body = {
        id_empresa: selectedCompany,
        suplidor_id: String(suplidorSel.id),
        fecha,
        tipodepago: TIPO_PAGO_AVANCE,
        valor: valorNum,
        forma_pago_documento: formaPago,
        referencia_pago: (referenciaPago || '').trim(),
        ncf: (ncf || '').trim(),
        tipodocumento: TIPO_DOCUMENTO_MOTOR_AVANCES,
        forma_pago: formaPago,
        usuario: usuarioLog || '',
        password: claveLog || '',
        modulos,
      };
      const response = await fetch(`${servidor}/contabilidadbackend_guardaravancesuplidor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo guardar el avance');
        return;
      }
      const idNuevo = data.id != null && data.id !== '' ? String(data.id) : '';
      showSuccess(
        idNuevo
          ? `${data.mensaje || 'Avance registrado'} · Nº documento / recibo (id): ${idNuevo} · nfact vacío hasta aplicar a factura`
          : data.mensaje || 'Avance registrado'
      );
      setValorStr('');
      setNcf('');
      setReferenciaPago('');
      await cargarAvances();
      if (typeof onGuardado === 'function') onGuardado();
    } catch {
      showError('Error de conexión al guardar');
    } finally {
      setGuardando(false);
    }
  }, [
    puedeModificar,
    servidor,
    selectedCompany,
    fecha,
    wanomesContableAbierto,
    suplidorSel,
    valorStr,
    formaPago,
    formasPagoDb.length,
    requiereReferencia,
    referenciaPago,
    ncf,
    modulos,
    usuarioLog,
    claveLog,
    showError,
    showSuccess,
    onGuardado,
    cargarAvances,
  ]);


  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 1.2,
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  const navyBtnSx = {
    height: 38,
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    borderRadius: 1.4,
    textTransform: 'none',
    fontWeight: 700,
    boxShadow: 'none',
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
  };

  const ctaCerrar = esDialogo ? (
    <IconButton
      onClick={onClose}
      disabled={guardando}
      aria-label="Cerrar"
      sx={{ color: FP.color.textSecondary }}
    >
      <CloseIcon />
    </IconButton>
  ) : null;

  const envolver = (contenido) => {
    if (!esDialogo) return contenido;
    return (
      <Dialog
        open={Boolean(open)}
        onClose={guardando ? undefined : onClose}
        maxWidth={false}
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, md: 1.5 },
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
            m: { xs: 0, md: 1 },
            width: { xs: '100vw', md: 'calc(100vw - 16px)' },
            maxWidth: '100vw',
            height: { xs: '100vh', md: 'calc(100vh - 16px)' },
            maxHeight: { xs: '100vh', md: 'calc(100vh - 16px)' },
          },
        }}
      >
        {contenido}
      </Dialog>
    );
  };

  if (!puedeLeer) {
    return envolver(
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Avances a suplidor"
          subtitle={
            nombreNegocio ? `${nombreNegocio} · Registro en avancesup` : 'Registro en avancesup'
          }
          cta={ctaCerrar}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «avancessuplidor» en roles para registrar avances a suplidor.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeOperarDb) {
    return envolver(
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Avances a suplidor"
          subtitle={
            nombreNegocio ? `${nombreNegocio} · Registro en avancesup` : 'Registro en avancesup'
          }
          cta={ctaCerrar}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card elevation={0} sx={{ ...cardShellSx, maxWidth: 560, width: '100%', p: { xs: 3, md: 4 } }}>
            <Alert severity="info">
              Seleccione una empresa con base de datos asignada para continuar.
            </Alert>
          </Card>
        </Box>
      </Box>
    );
  }

  return envolver(
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Avances a suplidor"
        subtitle={nombreNegocio ? `${nombreNegocio} · Registro en avancesup` : 'Registro en avancesup'}
        cta={ctaCerrar}
      />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SavingsIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Datos del avance
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={imprimirAvanceGuardado}
              disabled={!avanceListadoSel}
              sx={navyBtnSx}
              className="no-print"
            >
              Imprimir documento guardado
            </Button>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {puedeLeer && puedeOperarDb && !cargandoFormas && formasPagoDb.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No hay formas de pago configuradas. Créelas en{' '}
              <strong>Configuración → Gestión de Formas de Pago</strong> (documento = texto que se guardará en el
              movimiento, p. ej. «Efectivo» o «Transferencia»).
            </Alert>
          )}

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={suplidores}
                  loading={cargandoSupl}
                  getOptionLabel={(o) => opcionLabel(o)}
                  value={suplidorSel}
                  onChange={(_, v) => setSuplidorSel(v)}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Suplidor"
                      placeholder="id-nombre-rnc"
                      size="small"
                      helperText="Maestros tipo suplidor (maestcta)"
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha"
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  error={!validacionFecha.ok}
                  helperText={!validacionFecha.ok ? validacionFecha.message : 'Período contable (varinicial / sesión)'}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Valor del avance"
                  size="small"
                  value={valorStr}
                  onChange={(e) => setValorStr(e.target.value)}
                  placeholder="0.00"
                  inputProps={{ inputMode: 'decimal' }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                  label="NCF u observación (opcional)"
                  size="small"
                  value={ncf}
                  onChange={(e) => setNcf(e.target.value.slice(0, 30))}
                  helperText="Hasta 30 caracteres (campo ncf)"
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2, mb: 1 }} className="no-print">
              El tipo de pago de los avances a suplidor es siempre <strong>Contado</strong>. El{' '}
              <strong>número de documento / recibo</strong> es el <strong>id</strong> del registro en la tabla
              avancesup; el campo <strong>nfact</strong> queda en blanco para enlazarlo al aplicar el avance a una
              factura.
            </Alert>

            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Forma de pago"
                  value={formaPago}
                  onChange={(e) => {
                    setFormaPago(e.target.value);
                    setReferenciaPago('');
                  }}
                  disabled={cargandoFormas || formasPagoDb.length === 0}
                  helperText={
                    cargandoFormas
                      ? 'Cargando…'
                      : filaFormaPagoActual
                        ? requiereReferencia
                          ? 'Debe completar referencia'
                          : 'No requiere referencia'
                        : ' '
                  }
                  SelectProps={{ displayEmpty: true }}
                  sx={fpInputSx}
                >
                  {formasPagoDb.length === 0 ? (
                    <MenuItem value="" disabled>
                      Sin opciones
                    </MenuItem>
                  ) : (
                    formasPagoDb.map((fp) => (
                      <MenuItem key={fp.id} value={fp.documento || ''}>
                        {fp.documento || '(sin nombre)'}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={5}>
                <TextField
                  fullWidth
                  label="Referencia al pago"
                  size="small"
                  value={requiereReferencia ? referenciaPago : ''}
                  onChange={(e) => setReferenciaPago(e.target.value)}
                  placeholder={requiereReferencia ? 'Nº cheque, transferencia, etc.' : ''}
                  disabled={!requiereReferencia}
                  required={requiereReferencia}
                  error={requiereReferencia && !(referenciaPago || '').trim()}
                  helperText={
                    requiereReferencia
                      ? 'Obligatorio: según configuración de la forma de pago'
                      : 'Deshabilitado: esta forma de pago no exige referencia'
                  }
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  disabled={!puedeModificar || guardando}
                  onClick={handleGuardar}
                  sx={{ ...navyBtnSx, width: '100%' }}
                >
                  Guardar avance
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={cardShellSx}>
          <Box sx={filterHeaderSx}>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Avances registrados{suplidorSel ? ' (suplidor seleccionado)' : ' (todos, límite 150)'}
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem', mb: 1.5 }}>
            Seleccione una fila o el círculo a la izquierda y use <strong>Imprimir documento guardado</strong>. Use el
            icono de papelera en cada fila para eliminar ese avance (si las reglas de negocio lo permiten).
          </Typography>
          {cargandoLista ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
            </Box>
          ) : filas.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
              <SavingsIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
              <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                No hay datos para mostrar
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                No hay avances registrados en este listado.
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
            >
              <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 1180, sm: 1360 } }}>
                <colgroup>
                  {['6%', '9%', '16%', '8%', '10%', '9%', '8%', '9%', '11%', '8%', '6%'].map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <TableBody>
                  <TableRow
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      '& td': {
                        border: 'none',
                        borderBottom: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                      },
                    }}
                  >
                    <TableCell />
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Fecha
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Suplidor
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Nº doc.
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Nº aplic. factura
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                        Valor
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Tipo
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Forma
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Referencia
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                        NCF / e-CF
                      </Typography>
                    </TableCell>
                    <TableCell className="no-print">
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                        Acciones
                      </Typography>
                    </TableCell>
                  </TableRow>
                    {filas.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        selected={avanceListadoSel?.id === row.id}
                        onClick={() => setAvanceListadoSel(row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Radio
                            checked={avanceListadoSel?.id === row.id}
                            onChange={() => setAvanceListadoSel(row)}
                            value={String(row.id)}
                            inputProps={{ 'aria-label': `Seleccionar avance id ${row.id}` }}
                          />
                        </TableCell>
                        <TableCell align="center">{formatearFecha(row.fecha)}</TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={row.nombre_suplidor || row.suplidor || '—'}
                        >
                          {row.nombre_suplidor || row.suplidor}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary }}>
                          {row.id != null ? String(row.id) : '—'}
                        </TableCell>
                        <TableCell>{(row.nfact || '').trim() || '—'}</TableCell>
                        <TableCell align="right">${fmtMoney(row.valor)}</TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={row.tipodepago || '—'}
                        >
                          {row.tipodepago || '—'}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={row.banco || '—'}
                        >
                          {row.banco || '—'}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={row.referencia || '—'}
                        >
                          {row.referencia || '—'}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                          }}
                          title={row.comprobantefiscal || row.ncf || '—'}
                        >
                          {row.comprobantefiscal || row.ncf || '—'}
                        </TableCell>
                        <TableCell align="center" className="no-print" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title={puedeModificar ? 'Eliminar avance' : 'Sin permiso para eliminar'}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={`Eliminar avance ${row.id}`}
                                disabled={!puedeModificar || eliminandoId != null}
                                onClick={(e) => handleEliminarAvance(row, e)}
                              >
                                {eliminandoId === row.id ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : (
                                  <DeleteOutlineIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </CardContent>
        </Card>
      </Box>

    </Box>
  );
};

export default AvancesSuplidor;
